import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  DollarSign,
  TrendingUp,
  FileText,
  Video,
  BookOpen,
  Copy,
  ExternalLink,
  Plus,
  Edit,
  Trash2,
  Eye,
  Heart,
  Award,
  Link as LinkIcon,
  Share2,
  BarChart3,
  Wallet,
  Clock,
  CheckCircle,
  Trophy,
  Medal,
  Crown,
  Star,
  Flame
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Leaderboard Component
const LeaderboardSection = ({ t }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('score');

  useEffect(() => {
    fetchLeaderboard();
  }, [sortBy]);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/educators/leaderboard?sort_by=${sortBy}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bold text-muted-foreground">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-slate-400/20 border-gray-400/30';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-600/30';
    return 'bg-card';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            {t('Educator Leaderboard', 'Orodha ya Walimu Bora')}
          </h2>
          <p className="text-muted-foreground">
            {t('Top financial educators ranked by impact and engagement', 'Walimu bora wa fedha kulingana na athari na ushiriki')}
          </p>
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48" data-testid="leaderboard-sort">
            <SelectValue placeholder={t('Sort by', 'Panga kwa')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="score">{t('Overall Score', 'Alama Jumla')}</SelectItem>
            <SelectItem value="referrals">{t('Most Referrals', 'Rufaa Nyingi')}</SelectItem>
            <SelectItem value="content">{t('Most Content', 'Maudhui Mengi')}</SelectItem>
            <SelectItem value="views">{t('Most Views', 'Maoni Mengi')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leaderboard List */}
      {leaderboard.length > 0 ? (
        <div className="space-y-3">
          {leaderboard.map((educator, idx) => (
            <Card 
              key={educator.id} 
              className={`${getRankBg(educator.rank)} transition-all hover:shadow-md`}
              data-testid={`leaderboard-rank-${educator.rank}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-background/50 flex items-center justify-center">
                    {getRankIcon(educator.rank)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-lg truncate">{educator.full_name}</h3>
                      {educator.rank <= 3 && (
                        <Badge className={
                          educator.rank === 1 ? 'bg-yellow-500' :
                          educator.rank === 2 ? 'bg-gray-400' : 'bg-amber-600'
                        }>
                          {educator.rank === 1 ? '🥇 Gold' : educator.rank === 2 ? '🥈 Silver' : '🥉 Bronze'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {educator.expertise?.slice(0, 3).map((exp, i) => (
                        <Badge key={i} variant="outline" className="text-xs capitalize">
                          {exp.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-blue-600">
                        <Users className="w-4 h-4" />
                        <span className="font-bold">{educator.stats.total_referrals}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('Referrals', 'Rufaa')}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-purple-600">
                        <FileText className="w-4 h-4" />
                        <span className="font-bold">{educator.stats.content_count}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('Content', 'Maudhui')}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-emerald-600">
                        <Eye className="w-4 h-4" />
                        <span className="font-bold">{educator.stats.total_views.toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('Views', 'Maoni')}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-red-500">
                        <Heart className="w-4 h-4" />
                        <span className="font-bold">{educator.stats.total_likes}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('Likes', 'Kupenda')}</p>
                    </div>
                  </div>

                  {/* Score */}
                  <div className="flex-shrink-0 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Flame className="w-5 h-5 text-orange-500" />
                      <span className="text-2xl font-bold text-primary">{Math.round(educator.score)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('Score', 'Alama')}</p>
                  </div>
                </div>

                {/* Mobile Stats */}
                <div className="flex md:hidden items-center justify-between mt-3 pt-3 border-t text-sm">
                  <div className="flex items-center gap-1 text-blue-600">
                    <Users className="w-4 h-4" />
                    <span>{educator.stats.total_referrals}</span>
                  </div>
                  <div className="flex items-center gap-1 text-purple-600">
                    <FileText className="w-4 h-4" />
                    <span>{educator.stats.content_count}</span>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600">
                    <Eye className="w-4 h-4" />
                    <span>{educator.stats.total_views}</span>
                  </div>
                  <div className="flex items-center gap-1 text-red-500">
                    <Heart className="w-4 h-4" />
                    <span>{educator.stats.total_likes}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-muted/50">
          <CardContent className="py-12 text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">
              {t('No educators yet', 'Hakuna walimu bado')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('Be the first to join the educator program!', 'Kuwa wa kwanza kujiunga na programu ya walimu!')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Score Explanation */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Star className="w-5 h-5 text-yellow-500 mt-0.5" />
            <div>
              <p className="font-medium text-sm">{t('How scores are calculated', 'Alama zinakokotolewa vipi')}</p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'Score = Referrals × 10 + Converted × 25 + Content × 5 + Views ÷ 10 + Likes × 2',
                  'Alama = Rufaa × 10 + Waliobadilika × 25 + Maudhui × 5 + Maoni ÷ 10 + Kupenda × 2'
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Educators = () => {
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const [isEducator, setIsEducator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [content, setContent] = useState([]);
  const [showRegister, setShowRegister] = useState(false);
  const [showCreateContent, setShowCreateContent] = useState(false);
  
  // Registration form
  const [regForm, setRegForm] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: '',
    expertise: [],
    social_links: {},
    document_url: ''
  });

  // Content form
  const [contentForm, setContentForm] = useState({
    title: '',
    content_type: 'article',
    description: '',
    content_url: '',
    content_body: '',
    thumbnail_url: '',
    tags: [],
    is_premium: false
  });

  const expertiseOptions = [
    { value: 'stocks', label: 'DSE Stocks' },
    { value: 'bonds', label: 'Government Bonds' },
    { value: 'treasury_bills', label: 'Treasury Bills' },
    { value: 'utt', label: 'UTT AMIS Funds' },
    { value: 'personal_finance', label: 'Personal Finance' },
    { value: 'risk_management', label: 'Risk Management' }
  ];

  useEffect(() => {
    checkEducatorStatus();
  }, []);

  const checkEducatorStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/educators/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setIsEducator(true);
        fetchDashboard();
        fetchContent();
      } else {
        setIsEducator(false);
        setLoading(false);
      }
    } catch (error) {
      setIsEducator(false);
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/educators/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/educators/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContent(data.content || []);
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regForm.document_url) {
      toast.error(t('Please provide a document URL', 'Tafadhali weka URL ya hati'));
      return;
    }
    if (regForm.expertise.length === 0) {
      toast.error(t('Please select at least one expertise area', 'Tafadhali chagua angalau eneo moja la utaalamu'));
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/educators/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(regForm)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(t('Successfully registered as educator!', 'Umefanikiwa kujiandikisha kama mwalimu!'));
        setShowRegister(false);
        setIsEducator(true);
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Failed to register');
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/educators/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...contentForm,
          tags: contentForm.tags.length > 0 ? contentForm.tags : ['general']
        })
      });

      if (res.ok) {
        toast.success(t('Content created successfully!', 'Maudhui yameundwa!'));
        setShowCreateContent(false);
        setContentForm({
          title: '',
          content_type: 'article',
          description: '',
          content_url: '',
          content_body: '',
          thumbnail_url: '',
          tags: [],
          is_premium: false
        });
        fetchContent();
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create content');
      }
    } catch (error) {
      toast.error('Failed to create content');
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm(t('Delete this content?', 'Futa maudhui haya?'))) return;
    
    try {
      const res = await fetch(`${API_URL}/api/educators/content/${contentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(t('Content deleted', 'Maudhui yamefutwa'));
        fetchContent();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${dashboard?.profile?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success(t('Referral link copied!', 'Kiungo cha rufaa kimenakiliwa!'));
  };

  const toggleExpertise = (value) => {
    if (regForm.expertise.includes(value)) {
      setRegForm(prev => ({
        ...prev,
        expertise: prev.expertise.filter(e => e !== value)
      }));
    } else {
      setRegForm(prev => ({
        ...prev,
        expertise: [...prev.expertise, value]
      }));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Not an educator - show registration
  if (!isEducator) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Award className="w-5 h-5" />
              <span className="font-medium">{t('Partner Program', 'Programu ya Washirika')}</span>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {t('Become a Financial Educator', 'Kuwa Mwalimu wa Fedha')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t(
                'Share your financial knowledge, create content, and earn 15% commission on every subscription from your referrals.',
                'Shiriki ujuzi wako wa kifedha, tengeneza maudhui, na upate kamisheni ya 15% kwa kila usajili kutoka rufaa zako.'
              )}
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">{t('15% Commission', 'Kamisheni 15%')}</h3>
                <p className="text-muted-foreground">
                  {t('Earn 15% of subscription revenue from users who sign up with your referral link', 
                     'Pata 15% ya mapato ya usajili kutoka kwa watumiaji wanaojiandikisha na kiungo chako')}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">{t('Create Content', 'Tengeneza Maudhui')}</h3>
                <p className="text-muted-foreground">
                  {t('Share videos, articles, and courses with our growing community of investors', 
                     'Shiriki video, makala, na kozi na jamii yetu inayokua ya wawekezaji')}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">{t('Build Audience', 'Jenga Hadhira')}</h3>
                <p className="text-muted-foreground">
                  {t('Grow your following and establish yourself as a financial education authority', 
                     'Ongeza wafuasi wako na jidhihirishe kama mamlaka ya elimu ya kifedha')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Leaderboard Preview */}
          <div className="mb-12">
            <LeaderboardSection t={t} />
          </div>

          {/* Registration Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                {t('Educator Application', 'Maombi ya Mwalimu')}
              </CardTitle>
              <CardDescription>
                {t('Complete the form below to join our educator program', 'Jaza fomu hapa chini kujiunga na programu yetu ya walimu')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Full Name', 'Jina Kamili')}</Label>
                    <Input
                      value={regForm.full_name}
                      onChange={e => setRegForm(prev => ({ ...prev, full_name: e.target.value }))}
                      required
                      data-testid="educator-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Email', 'Barua Pepe')}</Label>
                    <Input
                      type="email"
                      value={regForm.email}
                      onChange={e => setRegForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      data-testid="educator-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('Phone Number', 'Nambari ya Simu')}</Label>
                  <Input
                    value={regForm.phone}
                    onChange={e => setRegForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+255..."
                    required
                    data-testid="educator-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('Bio / About You', 'Kuhusu Wewe')}</Label>
                  <Textarea
                    value={regForm.bio}
                    onChange={e => setRegForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder={t('Tell us about your background and experience in finance...', 'Tueleze kuhusu historia yako na uzoefu wa kifedha...')}
                    rows={4}
                    required
                    data-testid="educator-bio-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('Areas of Expertise', 'Maeneo ya Utaalamu')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {expertiseOptions.map(opt => (
                      <Badge
                        key={opt.value}
                        variant={regForm.expertise.includes(opt.value) ? "default" : "outline"}
                        className="cursor-pointer hover:bg-primary/20 transition-colors"
                        onClick={() => toggleExpertise(opt.value)}
                        data-testid={`expertise-${opt.value}`}
                      >
                        {opt.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('Document URL (ID/Qualification)', 'URL ya Hati (Kitambulisho/Sifa)')}</Label>
                  <Input
                    value={regForm.document_url}
                    onChange={e => setRegForm(prev => ({ ...prev, document_url: e.target.value }))}
                    placeholder="https://drive.google.com/... or https://..."
                    required
                    data-testid="educator-document-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('Upload your ID or qualification to a cloud storage and paste the link', 'Pakia kitambulisho au sifa kwenye hifadhi ya wingu na ubandike kiungo')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t('Social Media Links (Optional)', 'Viungo vya Mitandao ya Kijamii (Hiari)')}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      placeholder="YouTube URL"
                      onChange={e => setRegForm(prev => ({
                        ...prev,
                        social_links: { ...prev.social_links, youtube: e.target.value }
                      }))}
                    />
                    <Input
                      placeholder="Twitter/X URL"
                      onChange={e => setRegForm(prev => ({
                        ...prev,
                        social_links: { ...prev.social_links, twitter: e.target.value }
                      }))}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" size="lg" data-testid="educator-submit-btn">
                  <Award className="w-5 h-5 mr-2" />
                  {t('Apply as Educator', 'Omba Kuwa Mwalimu')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Educator Dashboard
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {t('Educator Dashboard', 'Dashibodi ya Mwalimu')}
            </h1>
            <p className="text-muted-foreground">
              {t('Welcome back', 'Karibu tena')}, {dashboard?.profile?.full_name}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyReferralLink} data-testid="copy-referral-btn">
              <Copy className="w-4 h-4 mr-2" />
              {t('Copy Referral Link', 'Nakili Kiungo')}
            </Button>
            <Dialog open={showCreateContent} onOpenChange={setShowCreateContent}>
              <DialogTrigger asChild>
                <Button data-testid="create-content-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Create Content', 'Unda Maudhui')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('Create New Content', 'Unda Maudhui Mapya')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateContent} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('Title', 'Kichwa')}</Label>
                    <Input
                      value={contentForm.title}
                      onChange={e => setContentForm(prev => ({ ...prev, title: e.target.value }))}
                      required
                      data-testid="content-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Content Type', 'Aina ya Maudhui')}</Label>
                    <div className="flex gap-2">
                      {[
                        { value: 'article', icon: FileText, label: 'Article' },
                        { value: 'video', icon: Video, label: 'Video' },
                        { value: 'course', icon: BookOpen, label: 'Course' }
                      ].map(type => (
                        <Button
                          key={type.value}
                          type="button"
                          variant={contentForm.content_type === type.value ? "default" : "outline"}
                          size="sm"
                          onClick={() => setContentForm(prev => ({ ...prev, content_type: type.value }))}
                          data-testid={`content-type-${type.value}`}
                        >
                          <type.icon className="w-4 h-4 mr-1" />
                          {type.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Description', 'Maelezo')}</Label>
                    <Textarea
                      value={contentForm.description}
                      onChange={e => setContentForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      required
                      data-testid="content-description-input"
                    />
                  </div>

                  {contentForm.content_type === 'video' && (
                    <div className="space-y-2">
                      <Label>{t('Video URL', 'URL ya Video')}</Label>
                      <Input
                        value={contentForm.content_url}
                        onChange={e => setContentForm(prev => ({ ...prev, content_url: e.target.value }))}
                        placeholder="https://youtube.com/..."
                        data-testid="content-video-url"
                      />
                    </div>
                  )}

                  {contentForm.content_type === 'article' && (
                    <div className="space-y-2">
                      <Label>{t('Article Content', 'Maudhui ya Makala')}</Label>
                      <Textarea
                        value={contentForm.content_body}
                        onChange={e => setContentForm(prev => ({ ...prev, content_body: e.target.value }))}
                        rows={6}
                        placeholder={t('Write your article here...', 'Andika makala yako hapa...')}
                        data-testid="content-body-input"
                      />
                    </div>
                  )}

                  <Button type="submit" className="w-full" data-testid="submit-content-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('Create Content', 'Unda Maudhui')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('Total Earnings', 'Mapato Jumla')}</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    TZS {(dashboard?.earnings?.total_earnings || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('Pending', 'Inasubiri')}</p>
                  <p className="text-2xl font-bold text-amber-600">
                    TZS {(dashboard?.earnings?.pending_earnings || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('Referrals', 'Rufaa')}</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboard?.referrals?.total || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {dashboard?.referrals?.converted || 0} {t('converted', 'waliobadilika')} ({dashboard?.referrals?.conversion_rate || 0}%)
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('Content', 'Maudhui')}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboard?.content?.total || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {(dashboard?.content?.total_views || 0).toLocaleString()} {t('total views', 'maoni jumla')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Section */}
        <Card className="mb-8 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <Share2 className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">{t('Your Referral Link', 'Kiungo Chako cha Rufaa')}</h3>
                  <p className="text-sm text-muted-foreground font-mono">
                    {window.location.origin}/register?ref={dashboard?.profile?.referral_code}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyReferralLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  {t('Copy', 'Nakili')}
                </Button>
                <Badge variant="secondary" className="h-10 px-4 flex items-center">
                  {dashboard?.earnings?.commission_rate} {t('Commission', 'Kamisheni')}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-lg">
            <TabsTrigger value="overview" data-testid="tab-overview">
              {t('Overview', 'Muhtasari')}
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">
              {t('Content', 'Maudhui')}
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">
              {t('Referrals', 'Rufaa')}
            </TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
              <Trophy className="w-4 h-4 mr-1" />
              {t('Rankings', 'Orodha')}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Earnings Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    {t('Monthly Earnings', 'Mapato ya Kila Mwezi')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboard?.monthly_earnings || []}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="month_name" className="text-xs" />
                        <YAxis className="text-xs" tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip 
                          formatter={(value) => [`TZS ${value.toLocaleString()}`, 'Earnings']}
                          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        />
                        <Bar dataKey="earnings" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Commissions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                    {t('Recent Commissions', 'Kamisheni za Hivi Karibuni')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_commissions?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_commissions.map((commission, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium capitalize">{commission.subscription_plan} Plan</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(commission.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">
                              +TZS {commission.amount.toLocaleString()}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {commission.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{t('No commissions yet', 'Hakuna kamisheni bado')}</p>
                      <p className="text-sm">{t('Share your referral link to start earning!', 'Shiriki kiungo chako kuanza kupata!')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Content Stats */}
            <Card>
              <CardHeader>
                <CardTitle>{t('Content Performance', 'Utendaji wa Maudhui')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Video className="w-8 h-8 mx-auto mb-2 text-red-500" />
                    <p className="text-2xl font-bold">{dashboard?.content?.videos || 0}</p>
                    <p className="text-sm text-muted-foreground">{t('Videos', 'Video')}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold">{dashboard?.content?.articles || 0}</p>
                    <p className="text-sm text-muted-foreground">{t('Articles', 'Makala')}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                    <p className="text-2xl font-bold">{dashboard?.content?.courses || 0}</p>
                    <p className="text-sm text-muted-foreground">{t('Courses', 'Kozi')}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Eye className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="text-2xl font-bold">{(dashboard?.content?.total_views || 0).toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{t('Total Views', 'Maoni Jumla')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('Your Content', 'Maudhui Yako')}</CardTitle>
                <Button size="sm" onClick={() => setShowCreateContent(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t('New', 'Mpya')}
                </Button>
              </CardHeader>
              <CardContent>
                {content.length > 0 ? (
                  <div className="space-y-4">
                    {content.map((item) => (
                      <div key={item.id} className="flex items-start gap-4 p-4 border rounded-lg" data-testid={`content-item-${item.id}`}>
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          item.content_type === 'video' ? 'bg-red-500/10' :
                          item.content_type === 'article' ? 'bg-blue-500/10' : 'bg-purple-500/10'
                        }`}>
                          {item.content_type === 'video' && <Video className="w-6 h-6 text-red-500" />}
                          {item.content_type === 'article' && <FileText className="w-6 h-6 text-blue-500" />}
                          {item.content_type === 'course' && <BookOpen className="w-6 h-6 text-purple-500" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{item.title}</h4>
                            <Badge variant="outline" className="capitalize">{item.content_type}</Badge>
                            {item.is_premium && <Badge className="bg-amber-500">Premium</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {item.views}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" />
                              {item.likes}
                            </span>
                            <span>{new Date(item.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteContent(item.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">{t('No content yet', 'Hakuna maudhui bado')}</p>
                    <p className="mb-4">{t('Start creating educational content to share with the community', 'Anza kuunda maudhui ya elimu kushiriki na jamii')}</p>
                    <Button onClick={() => setShowCreateContent(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('Create Your First Content', 'Unda Maudhui Yako ya Kwanza')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>{t('Referred Users', 'Watumiaji Waliopendekezwa')}</CardTitle>
                <CardDescription>
                  {t('Users who signed up using your referral link', 'Watumiaji waliojisajili kwa kutumia kiungo chako cha rufaa')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard?.referrals?.recent?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.referrals.recent.map((referral, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{referral.user_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {t('Joined', 'Alijiunga')} {new Date(referral.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {referral.has_subscribed ? (
                            <>
                              <Badge className="bg-emerald-500">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {t('Subscribed', 'Amejiandikisha')}
                              </Badge>
                              <p className="text-sm text-emerald-600 mt-1">
                                +TZS {(referral.commission_paid || 0).toLocaleString()}
                              </p>
                            </>
                          ) : (
                            <Badge variant="outline">{t('Free User', 'Mtumiaji Bure')}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">{t('No referrals yet', 'Hakuna rufaa bado')}</p>
                    <p className="mb-4">{t('Share your referral link to start earning commissions', 'Shiriki kiungo chako kuanza kupata kamisheni')}</p>
                    <Button variant="outline" onClick={copyReferralLink}>
                      <Copy className="w-4 h-4 mr-2" />
                      {t('Copy Referral Link', 'Nakili Kiungo cha Rufaa')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <LeaderboardSection t={t} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Educators;
